import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useApiGet, prefetch } from "@/lib/apiCache";
import { GAMES, FAMILY } from "@/lib/games";
import { formatDate, formatTime24to12, cycleDateIST } from "@/lib/format";
import { CalendarBlank, UsersThree, ArrowRight, Clock, CircleNotch, ArrowClockwise } from "@phosphor-icons/react";

export default function GamePage({ slug }) {
  const game = GAMES.find((g) => g.slug === slug);
  const { data, refresh } = useApiGet(`/results/${slug}?limit=10`);
  const recent = data || [];
  const latest = recent[0] || null;
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Prefetch the Previous Result data so tapping the CTA opens instantly.
    if (slug) prefetch(`/results/${slug}?limit=500`);
    // Poll every 10s to mirror upstream changes almost instantly.
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [slug, refresh]);

  if (!game) return null;
  const f = FAMILY[game.color_family];
  const path = `/${slug}`;
  const expectedDate = cycleDateIST(game);
  const hasCurrent = latest && latest.result_date === expectedDate;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://teerjuwai.com/" },
      { "@type": "ListItem", position: 2, name: `${game.name} Result`, item: `https://teerjuwai.com${path}` },
    ],
  };

  async function handleRefresh() {
    setRefreshing(true);
    try { await refresh(); } finally { setTimeout(() => setRefreshing(false), 400); }
  }

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <SEO
        title={`${game.name} | ${game.name} Result | Teer Result Today | Live FR & SR Results`}
        description={`${game.name} Teer result today — live First Round (F/R at ${formatTime24to12(game.fr_time)} IST) and Second Round (S/R at ${formatTime24to12(game.sr_time)} IST) results, previous results and common number.`}
        path={path}
        jsonLd={jsonLd}
      />
      <Header />

      <section className={`${f.head}`}>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <nav className="text-[11px] uppercase tracking-widest opacity-80 flex items-center gap-1">
            <Link to="/" className="hover:underline">Home</Link>
            <span>›</span>
            <span>{game.name}</span>
          </nav>
          <h1 className="font-heading font-black tracking-tight text-2xl sm:text-4xl mt-1" data-testid="game-h1">
            {game.name} Result Today
          </h1>
          <p className="mt-1 opacity-90 text-xs sm:text-sm">
            FR {formatTime24to12(game.fr_time)} · SR {formatTime24to12(game.sr_time)} · IST · Cycle {formatDate(expectedDate)}
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6">
        {/* Latest result card */}
        <div className={`bg-white border-2 ${f.border} rounded-2xl overflow-hidden`}>
          <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Latest Result</div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-neutral-500">{hasCurrent ? formatDate(latest.result_date) : formatDate(expectedDate)}</div>
              <button onClick={handleRefresh} className={`text-neutral-500 hover:text-neutral-900 ${refreshing ? "animate-spin" : ""}`} aria-label="Refresh" data-testid="game-refresh">
                <ArrowClockwise size={16} weight="bold" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2">
            {[
              { label: "F/R", time: game.fr_time, value: latest?.fr },
              { label: "S/R", time: game.sr_time, value: latest?.sr },
            ].map((slot, i) => (
              <div key={slot.label} className={`p-6 text-center ${i === 0 ? "border-r border-neutral-100" : ""}`}>
                <div className={`inline-flex items-center gap-1 text-xs font-semibold ${f.num}`}>
                  <Clock size={12} weight="bold" /> {formatTime24to12(slot.time)}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold mt-1">{slot.label}</div>
                {slot.value ? (
                  <div className={`font-numbers font-black text-6xl sm:text-7xl leading-none mt-1 ${f.num}`}>{slot.value}</div>
                ) : (
                  <div className="mt-2 flex flex-col items-center">
                    <CircleNotch size={40} weight="bold" className={`${f.num} animate-spin`} />
                  </div>
                )}
                {!hasCurrent && (
                  <div className="mt-2 text-[11px] font-black uppercase tracking-widest text-neutral-500">Waiting Result</div>
                )}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 border-t border-neutral-100 bg-neutral-50">
            <Link to={`${path}/previous-result`} className="min-h-[48px] flex items-center justify-center gap-2 text-sm font-semibold hover:bg-white transition" data-testid="prev-cta">
              <CalendarBlank size={16} weight="bold" /> Previous Result <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to={`${path}/common-number`} className="min-h-[48px] flex items-center justify-center gap-2 text-sm font-semibold border-l border-neutral-200 hover:bg-white transition" data-testid="common-cta">
              <UsersThree size={16} weight="bold" /> Common Number <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>

        {/* Recent 10 results */}
        <div className="mt-8">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-neutral-900">Recent {game.name} Results</h2>
          <div className="mt-3 bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-4 py-2 font-bold">Date</th>
                  <th className="px-4 py-2 font-bold">F/R</th>
                  <th className="px-4 py-2 font-bold">S/R</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="px-4 py-2.5">{formatDate(r.result_date)}</td>
                    <td className={`px-4 py-2.5 font-numbers font-bold ${f.num}`}>{r.fr ?? "--"}</td>
                    <td className={`px-4 py-2.5 font-numbers font-bold ${f.num}`}>{r.sr ?? "--"}</td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-neutral-500">No results yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="font-heading text-lg font-bold">About {game.name}</h3>
          <p className="text-neutral-700 text-sm leading-relaxed mt-2">
            {game.name} belongs to the {game.category.replace(/ Results$/, "").toLowerCase()} category. First Round (FR) is scheduled at
            {" "}{formatTime24to12(game.fr_time)} IST and Second Round (SR) at {formatTime24to12(game.sr_time)} IST.
            Use the Previous Result page to browse historical FR/SR outcomes for {game.name}, and the Common Number
            page to see the auto-derived Direct/House/Ending breakdown for each recent date.
          </p>
          <p className="mt-3 text-xs text-neutral-500">
            Displayed numbers are informational. Please cross-verify with official sources.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
